import { AsyncPipe, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import dayjs from 'dayjs';
import { groupBy, map, range, reverse, sortBy } from 'lodash-es';
import { NgChartsModule } from 'ng2-charts';

import { TimelineQuery } from '@models/milestone-category/milestone/timeline.query';

import { IconComponent } from '@shared/components/icon/icon.component';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { listTimelineMilestonesQuery } from '@shared/services/gql.service';

import {
  chartOptions,
  createDatasets,
  createLegend,
  defaultChart,
} from './trial-insights-clinical-timeline-chart';
import { TrialTimelineMilestoneCategoryDates } from '../../models/trial-insights.model';
import { TrialInsightsHeaderComponent } from '../trial-insights-header/trial-insights-header.component';
import { TrialInsightsLegendComponent } from '../trial-insights-legend/trial-insights-legend.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TrialInsightsHeaderComponent,
    IconComponent,
    NgStyle,
    RouterLink,
    NgChartsModule,
    TrialInsightsLegendComponent,
    AsyncPipe,
  ],
  selector: 'aux-trial-insights-clinical-timeline',
  styleUrls: ['../../trial-insights.component.scss'],
  templateUrl: 'trial-insights-clinical-timeline.component.html',
})
export class TrialInsightsClinicalTimelineComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly timelineQuery = inject(TimelineQuery);

  chartOptions = defaultChart();
  color = '#3d444a';
  header = '';
  legendOptions = createLegend();
  loading$ = this.timelineQuery.selectLoading();
  milestoneCategoryDates: TrialTimelineMilestoneCategoryDates[] = [];
  milestoneCategoryNames: string[] = [];
  path = ROUTING_PATH.FORECAST_ROUTING;
  quarters: string[] = [];
  routerLink = `/${this.path.INDEX}/${this.path.TIMELINE}`;
  title = 'Trial Timeline';
  totalDays = 0;

  ngOnInit() {
    this.getTimelineInfo();
  }

  createBarChart() {
    if (!this.quarters.length || !this.totalDays) {
      return;
    }

    this.legendOptions = createLegend(this.milestoneCategoryNames);
    this.chartOptions.data.labels = this.milestoneCategoryNames;
    this.chartOptions.data.datasets = createDatasets(this.milestoneCategoryDates);
    this.chartOptions.options = chartOptions(
      this.totalDays,
      this.quarters,
      this.milestoneCategoryDates[0].startDate
    );

    this.cdr.detectChanges();
  }

  createMilestoneCategories(timelineData: listTimelineMilestonesQuery[]): void {
    const groupedTimelines = groupBy(timelineData, 'milestone.milestone_category_id');

    const sortedByStart: listTimelineMilestonesQuery[][] = sortBy(
      map(groupedTimelines, (milestones) => {
        return sortBy(milestones, (milestone) => dayjs(milestone.contract_start_date).toDate());
      }),
      (milestones) => {
        return milestones[0].contract_start_date;
      }
    );

    const sortedByEnd: listTimelineMilestonesQuery[][] = map(sortedByStart, (milestones) => {
      return reverse(
        sortBy(milestones, (milestone) => dayjs(milestone.contract_end_date).toDate())
      );
    });

    this.milestoneCategoryNames = [];
    this.milestoneCategoryDates = [];

    sortedByStart.forEach((milestones, index) => {
      const startMilestone = milestones[0];
      const endMilestone = sortedByEnd[index][0];

      const milestoneName = startMilestone.milestone.milestone_category.name;

      const startDate = startMilestone.contract_start_date;
      const endDate = endMilestone.contract_end_date;
      const totalMilestoneDays = dayjs(endDate).diff(dayjs(startDate), 'days');

      this.milestoneCategoryNames.push(milestoneName);

      this.milestoneCategoryDates.push({
        endDate,
        startDate,
        totalDays: totalMilestoneDays,
      });
    });
  }

  createQuarters(): void {
    if (!this.milestoneCategoryDates.length) {
      return;
    }

    const lastIndex = this.milestoneCategoryDates.length - 1;
    const timelineStart = this.milestoneCategoryDates[0].startDate;
    const timelineEnd = this.milestoneCategoryDates[lastIndex].endDate;
    const totalDays = dayjs(timelineEnd).diff(dayjs(timelineStart), 'days');

    this.totalDays = totalDays;

    const totalMonths = Math.round(totalDays / 30);

    this.header = totalMonths < 0 ? '' : `${totalMonths} Months`;

    const quarterStart = dayjs(timelineStart).startOf('quarter');
    const quarterEnd = dayjs(timelineEnd).endOf('quarter');
    const quartersFloat = dayjs(quarterEnd).diff(dayjs(quarterStart), 'quarters', true);

    if (!quartersFloat) {
      return;
    }

    const totalQuarters = Math.round(quartersFloat);

    this.quarters = range(totalQuarters).map((int) => {
      const date = quarterStart.add(int, 'quarter');
      const quarter = `Q${date.quarter()}`;
      const year = date.year();

      return `${quarter} ${year}`;
    });
  }

  getTimelineInfo(): void {
    this.timelineQuery
      .select('items')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((timelineData) => {
        this.createMilestoneCategories(timelineData);
        this.createQuarters();
        this.createBarChart();

        this.cdr.markForCheck();
      });
  }
}
